import { getErrorMessage, isAuthError, isTransientError } from '@brighte/passwordless-authenticator';
import { ErrorResponse } from '@graphql';
import { KNOWN_ERROR_MESSAGES as ERROR, KnownErrors } from '@utils/constants';

export { getErrorMessage };

type ErrorResult = { message: string; code?: number; isRetryable: boolean };

const errorsMap: { [key: string]: string } = {
  [KnownErrors.NotFound]: ERROR.INVALID_QUOTE,
  [KnownErrors.ValidationError]: ERROR.VALIDATION_ERROR,
  [KnownErrors.QuoteExpired]: ERROR.QUOTE_EXPIRED,
  [KnownErrors.InvalidPackageSelection]: ERROR.INVALID_PACKAGE_SELECTION,
};

export function getErrorDetails(error: ErrorResponse): ErrorResult {
  if (isAuthError(error)) {
    return { code: 401, message: 'UNAUTHENTICATED', isRetryable: false };
  }

  // A retry has already been attempted (see the mutation's own `retry` option) by the time the
  // customer sees this screen, so `isRetryable` here just decides whether to offer a manual
  // "Try again" instead of relying on the mutation quietly succeeding on its own.
  const isRetryable = isTransientError(error);
  const graphqlError = error?.response?.errors?.[0];

  if (!graphqlError) {
    // No GraphQL errors array at all — typically a raw HTTP failure from the gateway/LB (e.g. a
    // 502) rather than a GraphQL-layer error, so the status lives directly on the response.
    return { code: error?.response?.status, message: ERROR.DEFAULT, isRetryable };
  }

  const errorMessage = getErrorMessage(graphqlError);
  const errorDetails = graphqlError?.extensions;

  if (errorsMap[errorMessage]) {
    return {
      message: errorsMap[errorMessage],
      isRetryable,
    };
  }

  if (errorDetails?.response) {
    const { status, body } = errorDetails.response;
    switch (status) {
      case 500:
        return { code: status, message: ERROR.INTERNAL_SERVER_ERROR, isRetryable };
      case 404:
        return { code: status, message: ERROR.USER_NOT_FOUND, isRetryable };
      default:
        return { code: status, message: body?.code || body?.message || ERROR.DEFAULT, isRetryable };
    }
  }

  if (errorDetails?.code) {
    switch (errorDetails.code) {
      case 'UNAUTHORIZED':
        return {
          code: errorDetails.statusCode,
          message: errorDetails.message?.includes('suspended') ? ERROR.USER_SUSPENDED : ERROR.FORBIDDEN,
          isRetryable: false,
        };
      case 'UNAUTHENTICATED':
        return { code: errorDetails.statusCode, message: 'UNAUTHENTICATED', isRetryable: false };
      case 'FORBIDDEN':
        return { code: 403, message: ERROR.FORBIDDEN, isRetryable: false };
      default:
        return { message: ERROR.DEFAULT, isRetryable };
    }
  }

  return { message: ERROR.DEFAULT, isRetryable };
}
